export type ForecastFilterKey =
  | 'customer'
  | 'forecastEnd'
  | 'forecastStart'
  | 'frequency'
  | 'hierarchy'
  | 'leadTime'
  | 'model'
  | 'modelVersion'
  | 'product'
  | 'runDate'
  | 'shipFrom'
  | 'shipToGeography'
  | 'span'
  | 'target';

export type ForecastFilters = Partial<Record<ForecastFilterKey, string>> & {
  fcts?: string[];
};

const KEY_TO_PARAM = {
  customer: 'ship_to',
  forecastEnd: 'forecast_end',
  forecastStart: 'forecast_start',
  frequency: 'frequency',
  hierarchy: 'hierarchy',
  leadTime: 'lead_time',
  model: 'model',
  modelVersion: 'model_version',
  product: 'product_id',
  runDate: 'forecast_creation_time',
  shipFrom: 'ship_from',
  shipToGeography: 'ship_to_geography',
  span: 'span',
  target: 'forecast_target',
} as const;

const FCT_PARAM = 'fct';

export function readForecastFiltersFromSearchParams(sp: URLSearchParams): ForecastFilters {
  const customer = sp.get(KEY_TO_PARAM.customer) || '';
  const forecastStart = sp.get(KEY_TO_PARAM.forecastStart) || '';
  const forecastEnd = sp.get(KEY_TO_PARAM.forecastEnd) || '';
  const frequency = sp.get(KEY_TO_PARAM.frequency) || '';
  const hierarchy = sp.get(KEY_TO_PARAM.hierarchy) || '';
  const leadTime = sp.get(KEY_TO_PARAM.leadTime) || '';
  const model = sp.get(KEY_TO_PARAM.model) || '';
  const modelVersion = sp.get(KEY_TO_PARAM.modelVersion) || '';
  const product = sp.get(KEY_TO_PARAM.product) || '';
  const runDate = sp.get(KEY_TO_PARAM.runDate) || '';
  const shipFrom = sp.get(KEY_TO_PARAM.shipFrom) || '';
  const shipToGeography = sp.get(KEY_TO_PARAM.shipToGeography) || '';
  const span = sp.get(KEY_TO_PARAM.span) || '';
  const target = sp.get(KEY_TO_PARAM.target) || '';
  const fcts = sp.getAll(FCT_PARAM).filter(Boolean);

  const out: ForecastFilters = {};
  if (customer) out.customer = customer;
  if (forecastStart) out.forecastStart = forecastStart;
  if (forecastEnd) out.forecastEnd = forecastEnd;
  if (frequency) out.frequency = frequency;
  if (hierarchy) out.hierarchy = hierarchy;
  if (leadTime) out.leadTime = leadTime;
  if (model) out.model = model;
  if (modelVersion) out.modelVersion = modelVersion;
  if (product) out.product = product;
  if (runDate) out.runDate = runDate;
  if (shipFrom) out.shipFrom = shipFrom;
  if (shipToGeography) out.shipToGeography = shipToGeography;
  if (span) out.span = span;
  if (target) out.target = target;
  if (fcts.length) out.fcts = fcts;

  return out;
}

export function writeForecastFiltersToSearchParams(sp: URLSearchParams, filters: ForecastFilters) {
  sp.delete(KEY_TO_PARAM.customer);
  sp.delete(KEY_TO_PARAM.forecastStart);
  sp.delete(KEY_TO_PARAM.forecastEnd);
  sp.delete(KEY_TO_PARAM.frequency);
  sp.delete(KEY_TO_PARAM.hierarchy);
  sp.delete(KEY_TO_PARAM.leadTime);
  sp.delete(KEY_TO_PARAM.model);
  sp.delete(KEY_TO_PARAM.modelVersion);
  sp.delete(KEY_TO_PARAM.product);
  sp.delete(KEY_TO_PARAM.runDate);
  sp.delete(KEY_TO_PARAM.shipFrom);
  sp.delete(KEY_TO_PARAM.shipToGeography);
  sp.delete(KEY_TO_PARAM.span);
  sp.delete(KEY_TO_PARAM.target);
  sp.delete(FCT_PARAM);

  if (filters.customer) sp.set(KEY_TO_PARAM.customer, filters.customer);
  if (filters.forecastStart) sp.set(KEY_TO_PARAM.forecastStart, filters.forecastStart);
  if (filters.forecastEnd) sp.set(KEY_TO_PARAM.forecastEnd, filters.forecastEnd);
  if (filters.frequency) sp.set(KEY_TO_PARAM.frequency, filters.frequency);
  if (filters.hierarchy) sp.set(KEY_TO_PARAM.hierarchy, filters.hierarchy);
  if (filters.leadTime) sp.set(KEY_TO_PARAM.leadTime, filters.leadTime);
  if (filters.model) sp.set(KEY_TO_PARAM.model, filters.model);
  if (filters.modelVersion) sp.set(KEY_TO_PARAM.modelVersion, filters.modelVersion);
  if (filters.product) sp.set(KEY_TO_PARAM.product, filters.product);
  if (filters.runDate) sp.set(KEY_TO_PARAM.runDate, filters.runDate);
  if (filters.shipFrom) sp.set(KEY_TO_PARAM.shipFrom, filters.shipFrom);
  if (filters.shipToGeography) sp.set(KEY_TO_PARAM.shipToGeography, filters.shipToGeography);
  if (filters.span) sp.set(KEY_TO_PARAM.span, filters.span);
  if (filters.target) sp.set(KEY_TO_PARAM.target, filters.target);
  for (const fct of filters.fcts ?? []) sp.append(FCT_PARAM, fct);
}
